/**
 * Staff review queue for refill requests submitted through the patient
 * portal — the refill counterpart of `StaffRxReviewPanel`.
 *
 * Same root cause as the prescriptions bug: the Care-module request view
 * for this tab only ever showed `care_version` records (none of which
 * patients can create), and the real `/staff/portal/refills` endpoint was
 * additionally returning encrypted field blobs un-decrypted, so even a
 * correctly-wired UI would have shown gibberish instead of patient names
 * and medicines. Both are fixed here.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Loader2, PackageCheck, Truck, Pill, AlertTriangle } from 'lucide-react';

import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../lib/api';
import './care.css';

const STATUS_BADGE = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-sky-100 text-sky-700',
  ready: 'bg-emerald-100 text-emerald-700',
  collected: 'bg-slate-200 text-slate-700',
  rejected: 'bg-red-100 text-red-700',
};

const ACTIONS_FOR_STATUS = {
  pending: [
    { action: 'approve', label: 'Approve', icon: CheckCircle2, cls: 'bg-emerald-600 hover:bg-emerald-700' },
    { action: 'reject', label: 'Reject', icon: XCircle, cls: 'text-red-600 hover:bg-red-50', variant: 'outline' },
  ],
  approved: [
    { action: 'ready', label: 'Mark ready', icon: Truck, cls: 'bg-sky-600 hover:bg-sky-700' },
  ],
  ready: [
    { action: 'collected', label: 'Mark collected', icon: PackageCheck, cls: 'bg-slate-600 hover:bg-slate-700' },
  ],
};

export const StaffRefillReviewPanel = () => {
  const [status, setStatus] = useState('pending');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deciding, setDeciding] = useState(null); // { refill, action }
  const [note, setNote] = useState('');
  const [onTime, setOnTime] = useState(null); // true|false|null, only for delivery+collected
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/staff/portal/refills', { params: { status } })
      .then((r) => setRows(r.data.refills || []))
      .catch(() => toast.error('Could not load refill requests'))
      .finally(() => setLoading(false));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const openDecision = (refill, action) => { setDeciding({ refill, action }); setNote(''); setOnTime(null); };
  const isDeliveryCompletion = deciding?.action === 'collected' && deciding?.refill?.delivery;

  const decide = async () => {
    if (!deciding) return;
    if (isDeliveryCompletion && onTime === null) {
      toast.error('Mark this delivery as on-time or late to continue');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/staff/portal/refills/${deciding.refill.request_id}/decision`, {
        action: deciding.action, note: note.trim(),
        ...(isDeliveryCompletion ? { on_time: onTime } : {}),
      });
      toast.success(
        { approve: 'Refill approved', reject: 'Refill rejected', ready: 'Marked ready for pickup', collected: 'Marked collected' }[deciding.action],
      );
      setDeciding(null); setNote(''); setOnTime(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="staff-refill-review-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2>Refill requests</h2>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[160px]" data-testid="staff-refill-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="pending" data-testid="staff-refill-status-pending">Awaiting review</SelectItem>
            <SelectItem value="approved" data-testid="staff-refill-status-approved">Approved</SelectItem>
            <SelectItem value="ready" data-testid="staff-refill-status-ready">Ready for pickup</SelectItem>
            <SelectItem value="collected" data-testid="staff-refill-status-collected">Collected</SelectItem>
            <SelectItem value="rejected" data-testid="staff-refill-status-rejected">Rejected</SelectItem>
            <SelectItem value="all" data-testid="staff-refill-status-all">All</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading && <p className="care-empty" data-testid="staff-refill-loading"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</p>}
      {!loading && rows.length === 0 && <p className="care-empty" data-testid="staff-refill-empty">Nothing in this queue.</p>}

      <div className="space-y-3">
        {rows.map((r) => {
          const actions = ACTIONS_FOR_STATUS[r.status] || [];
          return (
            <article key={r.request_id} className="care-record" data-testid={`staff-refill-${r.request_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="flex gap-3 items-start min-w-0">
                  <div className="p-2 rounded-lg bg-slate-100 text-slate-600 shrink-0"><Pill className="w-5 h-5" /></div>
                  <div className="min-w-0">
                    <div className="font-medium truncate" data-testid={`staff-refill-patient-${r.request_id}`}>{r.patient_name}</div>
                    <div className="text-xs text-slate-500">
                      {r.patient_phone && <>{r.patient_phone} · </>}{new Date(r.created_at).toLocaleString()}
                      {r.delivery && <> · Delivery requested</>}
                      {r.preferred_pickup_date && <> · Preferred: {r.preferred_pickup_date}</>}
                    </div>
                  </div>
                </div>
                <Badge className={STATUS_BADGE[r.status] || STATUS_BADGE.pending} data-testid={`staff-refill-badge-${r.request_id}`}>{r.status}</Badge>
              </div>

              {!!(r.items || []).length && (
                <ul className="mt-2 text-sm text-slate-700 list-disc list-inside" data-testid={`staff-refill-items-${r.request_id}`}>
                  {r.items.map((it, i) => (
                    <li key={i}>
                      {it.product_name}{it.quantity ? ` × ${it.quantity}` : ''}
                      {it.dose && <span className="text-slate-500"> — {it.dose}{it.frequency ? `, ${it.frequency}` : ''}{it.strength ? ` (${it.strength})` : ''}</span>}
                    </li>
                  ))}
                </ul>
              )}
              {r.dose_check && (
                <div className={`mt-2 rounded-lg border px-3 py-2 text-xs ${r.dose_check.flagged ? 'border-red-300 bg-red-50 text-red-800' : 'border-emerald-200 bg-emerald-50 text-emerald-800'}`}
                  data-testid={`staff-refill-dose-check-${r.request_id}`}>
                  <div className="font-semibold flex items-center gap-1">
                    {r.dose_check.flagged ? <AlertTriangle className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                    Child dose check · age {Math.floor(r.dose_check.age_years)}
                  </div>
                  <ul className="mt-1 space-y-0.5">
                    {r.dose_check.weight?.message && (
                      <li data-testid={`staff-refill-dose-weight-${r.request_id}`}>
                        {r.dose_check.weight.status === 'unlikely' ? '⚠ ' : ''}{r.dose_check.weight.message}
                      </li>
                    )}
                    {(r.dose_check.items || []).filter((d) => d.message).map((d, idx) => (
                      <li key={idx} data-testid={`staff-refill-dose-line-${r.request_id}-${idx}`}>
                        {(d.status === 'high' || d.status === 'low') ? '⚠ ' : ''}{d.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {r.notes && <p className="text-sm text-slate-700 mt-1">{r.notes}</p>}
              {r.decision?.note && (
                <p className="text-sm border-t pt-2 mt-2 whitespace-pre-wrap" data-testid={`staff-refill-decision-note-${r.request_id}`}>{r.decision.note}</p>
              )}

              {!!actions.length && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {actions.map(({ action, label, icon: ActIcon, cls, variant }) => (
                    <Button
                      key={action} size="sm" variant={variant} className={variant ? cls : `${cls} text-white`}
                      onClick={() => openDecision(r, action)} data-testid={`staff-refill-${action}-${r.request_id}`}
                    >
                      <ActIcon className="w-4 h-4 mr-1" /> {label}
                    </Button>
                  ))}
                </div>
              )}
            </article>
          );
        })}
      </div>

      <Dialog open={!!deciding} onOpenChange={(o) => { if (!o) { setDeciding(null); setNote(''); setOnTime(null); } }}>
        <DialogContent data-testid="staff-refill-decision-dialog" closeProps={{ 'data-testid': 'staff-refill-decision-close' }}>
          <DialogHeader>
            <DialogTitle>
              {{ approve: 'Approve refill', reject: 'Reject refill', ready: 'Mark ready for pickup', collected: 'Mark as collected' }[deciding?.action]}
            </DialogTitle>
            <DialogDescription>The patient will be notified where applicable.</DialogDescription>
          </DialogHeader>
          <Textarea
            rows={3} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note…" data-testid="staff-refill-decision-note-input"
          />
          {isDeliveryCompletion && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-700">Was this delivery on time?</p>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant={onTime === true ? 'default' : 'outline'}
                  className={onTime === true ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                  onClick={() => setOnTime(true)} data-testid="staff-refill-delivery-on-time">
                  On time
                </Button>
                <Button type="button" size="sm" variant={onTime === false ? 'default' : 'outline'}
                  className={onTime === false ? 'bg-red-600 hover:bg-red-700' : ''}
                  onClick={() => setOnTime(false)} data-testid="staff-refill-delivery-late">
                  Late
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeciding(null); setNote(''); setOnTime(null); }}>Cancel</Button>
            <Button onClick={decide} disabled={saving || (isDeliveryCompletion && onTime === null)} data-testid="staff-refill-decision-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
